import { motion } from 'framer-motion';
import { useEffect } from 'react';
import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';
import WhatsAppWidget from '../../components/feature/WhatsAppWidget';
import CookieConsent from '../../components/feature/CookieConsent';
import { updateSEO, getBaseUrl } from '../../utils/seo';

const ContactPage = () => {
  useEffect(() => {
    const baseUrl = getBaseUrl();
    
    updateSEO({
      title: 'İletişim | Bursa Sulama Sistemleri - Görükle Nilüfer',
      description: 'Bursa Sulama Sistemleri ile iletişime geçin. Adres: Atatürk Caddesi No:72, Görükle/Nilüfer/Bursa. Telefon: 0507 134 46 68. Ücretsiz keşif ve danışmanlık için hemen arayın.',
      keywords: 'bursa sulama sistemleri iletişim, görükle sulama firması telefon, nilüfer sulama şirketi adres, bursa sulama sistemleri randevu',
      canonical: `${baseUrl}/iletisim`,
      ogImage: 'https://static.readdy.ai/image/f7a5d02425e0675ec2bf06c3a77d9570/447d769c117baf03cd3b358fbf217ae4.png',
      schema: {
        '@context': 'https://schema.org',
        '@type': 'ContactPage',
        name: 'İletişim - Bursa Sulama Sistemleri',
        description: 'Bursa Sulama Sistemleri iletişim bilgileri ve konum',
        url: `${baseUrl}/iletisim`,
        mainEntity: {
          '@type': 'LocalBusiness',
          name: 'Bursa Sulama Sistemleri',
          telephone: '+905071344668',
          address: {
            '@type': 'PostalAddress',
            streetAddress: 'Atatürk Caddesi No:72, Hasan Boztürk Apartmanı',
            addressLocality: 'Görükle, Nilüfer',
            addressRegion: 'Bursa',
            postalCode: '16000',
            addressCountry: 'TR'
          },
          geo: {
            '@type': 'GeoCoordinates',
            latitude: '40.223456',
            longitude: '28.876543'
          },
          openingHoursSpecification: [
            {
              '@type': 'OpeningHoursSpecification',
              dayOfWeek: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday'],
              opens: '08:00',
              closes: '18:00'
            },
            {
              '@type': 'OpeningHoursSpecification',
              dayOfWeek: 'Saturday',
              opens: '09:00',
              closes: '17:00'
            }
          ]
        }
      }
    });
  }, []);

  return (
    <div className="min-h-screen bg-white">
      <Navbar />

      {/* Hero Section */}
      <section className="pt-24 md:pt-32 pb-12 md:pb-20 bg-gradient-to-br from-[#14B8A6]/10 to-white">
        <div className="max-w-[1400px] mx-auto px-4 md:px-6 lg:px-12">
          <motion.div
            initial={{ opacity: 1, y: 0 }}
            className="text-center max-w-4xl mx-auto"
          >
            <h1 className="text-3xl md:text-5xl lg:text-7xl font-bold text-[#0A1628] mb-4 md:mb-6">
              İletişim
            </h1>
            <p className="text-base md:text-xl lg:text-2xl text-gray-600 leading-relaxed px-4">
              Profesyonel sulama çözümleri için bize ulaşın
            </p>
          </motion.div>
        </div>
      </section>

      {/* Contact Section */}
      <section className="py-12 md:py-20 lg:py-32 bg-[#F8F6F3]">
        <div className="max-w-[1400px] mx-auto px-4 md:px-6 lg:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-5 gap-8 md:gap-12">
            {/* Contact Info */}
            <motion.div
              initial={{ opacity: 1, x: 0 }}
              className="lg:col-span-2"
            >
              <div className="flex items-center gap-2 mb-4 md:mb-6">
                <div className="w-2 h-2 bg-[#14B8A6] rounded-full"></div>
                <span className="text-xs md:text-sm text-[#0A1628] font-medium uppercase tracking-wider">
                  İletişim
                </span>
              </div>
              <h2 className="text-2xl md:text-4xl lg:text-5xl font-bold text-[#0A1628] mb-8 md:mb-12">
                Bize Ulaşın
              </h2>

              <div className="space-y-6 md:space-y-8">
                {/* Address */}
                <div className="flex gap-4 md:gap-6">
                  <div className="w-10 h-10 md:w-12 md:h-12 bg-[#14B8A6] rounded-xl flex items-center justify-center flex-shrink-0">
                    <i className="ri-map-pin-line text-xl md:text-2xl text-white"></i>
                  </div>
                  <div>
                    <h3 className="text-lg md:text-xl font-bold text-[#0A1628] mb-2">
                      Adres
                    </h3>
                    <p className="text-sm md:text-base text-gray-600 leading-relaxed">
                      Atatürk Caddesi No:72
                      <br />
                      Hasan Boztürk Apartmanı
                      <br />
                      Görükle / Nilüfer / Bursa
                    </p>
                  </div>
                </div>

                {/* Phone */}
                <div className="flex gap-4 md:gap-6">
                  <div className="w-10 h-10 md:w-12 md:h-12 bg-[#14B8A6] rounded-xl flex items-center justify-center flex-shrink-0">
                    <i className="ri-phone-line text-xl md:text-2xl text-white"></i>
                  </div>
                  <div>
                    <h3 className="text-lg md:text-xl font-bold text-[#0A1628] mb-2">
                      Telefon
                    </h3>
                    <a
                      href="tel:+905071344668"
                      className="text-xl md:text-2xl text-[#14B8A6] hover:text-[#0F9B8E] font-semibold transition-colors"
                    >
                      +90 507 134 46 68
                    </a>
                  </div>
                </div>

                {/* Working Hours */}
                <div className="flex gap-4 md:gap-6">
                  <div className="w-10 h-10 md:w-12 md:h-12 bg-[#14B8A6] rounded-xl flex items-center justify-center flex-shrink-0">
                    <i className="ri-time-line text-xl md:text-2xl text-white"></i>
                  </div>
                  <div>
                    <h3 className="text-lg md:text-xl font-bold text-[#0A1628] mb-2">
                      Çalışma Saatleri
                    </h3>
                    <p className="text-sm md:text-base text-gray-600">
                      Hafta İçi: 08:00 - 18:00
                      <br />
                      Cumartesi: 09:00 - 17:00
                      <br />
                      Pazar: Kapalı
                    </p>
                  </div>
                </div>

                {/* WhatsApp */}
                <div className="flex gap-4 md:gap-6">
                  <div className="w-10 h-10 md:w-12 md:h-12 bg-[#25D366] rounded-xl flex items-center justify-center flex-shrink-0">
                    <i className="ri-whatsapp-line text-xl md:text-2xl text-white"></i>
                  </div>
                  <div>
                    <h3 className="text-lg md:text-xl font-bold text-[#0A1628] mb-2">
                      WhatsApp
                    </h3>
                    <a
                      href="https://wa.me/905071344668?text=Merhaba,%20ben%20sizinle%20bursasulamasistemleri.com%20sitesinden%20iletişime%20geçiyorum."
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-base md:text-lg text-[#25D366] hover:text-[#1DA851] font-medium transition-colors"
                    >
                      Hemen Mesaj Gönder
                    </a>
                  </div>
                </div>
              </div>

              {/* Quick Contact Buttons */}
              <div className="mt-8 md:mt-12 space-y-3 md:space-y-4">
                <a
                  href="tel:+905071344668"
                  className="flex items-center justify-center gap-3 w-full bg-[#14B8A6] hover:bg-[#0F9B8E] text-white px-6 md:px-8 py-4 md:py-5 rounded-full text-base md:text-lg font-semibold transition-all duration-300 hover:scale-105 whitespace-nowrap"
                >
                  <i className="ri-phone-fill text-xl md:text-2xl"></i>
                  <span>Hemen Ara</span>
                </a>
                <a
                  href="https://wa.me/905071344668?text=Merhaba,%20ben%20sizinle%20bursasulamasistemleri.com%20sitesinden%20iletişime%20geçiyorum."
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center justify-center gap-3 w-full bg-[#25D366] hover:bg-[#1DA851] text-white px-6 md:px-8 py-4 md:py-5 rounded-full text-base md:text-lg font-semibold transition-all duration-300 hover:scale-105 whitespace-nowrap"
                >
                  <i className="ri-whatsapp-fill text-xl md:text-2xl"></i>
                  <span>WhatsApp Mesaj</span>
                </a>
              </div>
            </motion.div>

            {/* Map */}
            <motion.div
              initial={{ opacity: 1, x: 0 }}
              className="lg:col-span-3"
            >
              <div className="rounded-3xl overflow-hidden shadow-2xl h-full min-h-[400px] md:min-h-[600px]">
                <iframe
                  src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3048.4234567890123!2d28.8765432!3d40.2234567!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x0%3A0x0!2zNDDCsDEzJzI0LjQiTiAyOMKwNTInMzUuNiJF!5e0!3m2!1str!2str!4v1234567890123!5m2!1str!2str"
                  width="100%"
                  height="100%"
                  style={{ border: 0 }}
                  allowFullScreen
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                  title="Bursa Sulama Sistemleri Konum"
                ></iframe>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Service Areas */}
      <section className="py-12 md:py-20 bg-white">
        <div className="max-w-[1400px] mx-auto px-4 md:px-6 lg:px-12">
          <motion.div
            initial={{ opacity: 1, y: 0 }}
            className="text-center"
          >
            <h2 className="text-2xl md:text-4xl lg:text-5xl font-bold text-[#0A1628] mb-4 md:mb-6 px-4">
              Hizmet Verdiğimiz Bölgeler
            </h2>
            <p className="text-base md:text-xl text-gray-600 mb-8 md:mb-12 max-w-3xl mx-auto px-4">
              Bursa'nın tüm ilçelerinde profesyonel sulama sistemleri kurulumu
              ve bakım hizmeti sunuyoruz
            </p>
            <div className="flex flex-wrap justify-center gap-3 md:gap-4 px-4">
              {[
                'Nilüfer',
                'Görükle',
                'Osmangazi',
                'Yıldırım',
                'Mudanya',
                'Gemlik',
                'İnegöl',
                'Mustafakemalpaşa',
                'Karacabey',
                'Orhangazi',
                'İznik',
                'Yenişehir',
              ].map((area, index) => (
                <motion.span
                  key={index}
                  initial={{ opacity: 1, scale: 1 }}
                  className="px-4 md:px-6 py-2 md:py-3 bg-[#14B8A6]/10 text-[#0A1628] rounded-full font-medium hover:bg-[#14B8A6] hover:text-white transition-colors cursor-default text-sm md:text-base"
                >
                  {area}
                </motion.span>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      <Footer />
      <WhatsAppWidget />
      <CookieConsent />
    </div>
  );
};

export default ContactPage;
